import { InjectionToken } from '@angular/core';
import { AdminRole } from '@por/consumer-login/shared-lib';
import { RouteTo } from './route-to.model';

/* eslint-disable @typescript-eslint/naming-convention */
export interface ConsumerPortalAdminConfig {
    production?: boolean;
    customerId?: string;
    organizationId: string;
    organizationType: string;
    organizationName?: string;

    /**
     * Environment-specific relationship ID for Consumer Portal onboarding
     * - Production: '39500'
     * - Development/Stage: '62701'
     * Required for onboarding functionality. If not provided, onboarding features will be disabled.
     */
    relationshipId?: string;

    /**
     * Cognito JWT token for accessing GAPI/Cognito endpoints
     * Used by onboarding API to authenticate with GAPI services.
     * Format: JWT (3 base64-encoded sections separated by dots)
     *
     * NOTE: This is SEPARATE from Auth.AccessToken
     *   - cognitoToken → Used for GAPI/Cognito endpoint access
     *   - Auth.AccessToken → Used for Consumer Login authentication
     *
     * The Admin Panel passes this token through to the onboarding API.
     * It does NOT use this token directly.
     */
    cognitoToken?: string;

    contractId?: string;
    apiUrl?: string;
    uiUrl?: string;
    showLoader?: boolean;
    defaultSubDomain?: string; // Required for dev and local environments
    menu?: Partial<Menu>;
    header?: Partial<Header>;
    footer?: Partial<Footer>;
    consumerPortal?: {
        height: string;
        width: string;
    };
    styling?: {
        themeColors?: {
            primaryColor?: string;
            secondaryColor?: string;
        };
        menu?: unknown;
        header?: Styling;
        footer?: Styling;
    };
    Auth?: {
        accessToken?: string;
        /**
         * Access token for Consumer Login (CL) authentication
         * This is NOT the same as cognitoToken.
         * Used for authenticating with Consumer Portal/Consumer Login services.
         */
        AccessToken?: string; // Backward compatibility
        RefreshToken?: string;
        Email?: string;
        /**
         * Optional list of Consumer Portal admin pages this user may access.
         * Empty/undefined → all pages visible. One → menu hidden, auto-redirect to that page.
         * Multiple → menu shows only these pages. See ADMIN_ROLE_TO_TAB for the page keys.
         */
        Role?: AdminRole[];
    };
    enabledFeatures?: EnabledFeatures;
    hiddenFeatures?: string[]; // These are manually hidden features in DB by POR - overrides all
    initialInputConfigAccessToken?: string;
    routeTo?: RouteTo;
}

export interface EnabledFeatures {
    essentials?: unknown; // Currently the type is unknown because we don't know what all features will be there in essentials Admin.
    syrinx?: unknown; // Currently the type is unknown because we don't know what all features will be there in syrinx Admin.
    expert?: unknown; // Currently the type is unknown because we don't know what all features will be there in expert Admin.
}

export interface Styling {
    paragraph?: unknown;
    heading?: unknown;
    button?: unknown;
    buttonToggle?: unknown;
    table?: unknown;
    anchor?: unknown;
    primaryColor?: unknown;
    secondaryColor?: unknown;
}

export interface Menu {
    Mandatory: boolean;
}

export interface Header {
    headerTitle: string;
    headerTitlePosition: string;
    logoUrl: string;
    logoPosition: string;
    logoWidth: string;
}

export interface Footer {
    footerText: string;
    position: string;
}

export const CONSUMER_PORTAL_ADMIN_APP_CONFIG = new InjectionToken<ConsumerPortalAdminConfig>('Application admin config');
export const CPEnvironment = new InjectionToken('Consumer Portal Admin Environment config');
